<template lang="pug">
transition(name="page")
  section.page(
    :class="[{ 'page--current': current, 'not-sr-only': wordDoc && current, 'sr-only': wordDoc }, attrs.class]"
    :data-open-iframes="openIframes"
    :id="page.id"
    :style="attrs.style"
    ref="pageEl"
    v-if="current || wordDoc || audioDoc"
  )
    uc-nav-badges(:pageId="page.id")

    uc-quiz-progress(:pageId="page.id")

    .page__content(ref="contentEl")
      slot
    br.hidden(uc-word-only="")
    hr.hidden(uc-word-only="")
</template>

<script setup lang="ts">
import {
  computed,
  provide,
  ref,
  reactive,
  nextTick,
  inject,
  unref,
  useAttrs,
  watchEffect
} from 'vue';

import { useRoute } from 'vue-router';
import { setCompleted, setLocation } from '../lib/SCORM';
import { stop as stopAudio } from '../lib/audio';
import useTheme from '../stores/useTheme';
import { storeToRefs } from 'pinia';
import useContent from '../stores/useContent';
import { dispatchUcEvent } from '../lib/useUcEvent';
import isHidden from '../lib/isHidden';

const props = defineProps<{ page: Page }>();

const attrs = useAttrs();
const route = useRoute();
const pageEl = ref<HTMLElement>();
const contentEl = ref<HTMLElement>();
const wordDoc = inject('wordDoc');
const audioDoc = inject('audioDoc');

const current = computed(() => {
  if (props.page.id === 'welcome' && !route.params.page) {
    return true;
  }

  return props.page.id === route.params.page;
});

provide(
  'pageTitle',
  computed(() => props.page.title)
);

provide(
  'pageId',
  computed(() => props.page.id)
);

provide('page', reactive(props.page));

useTheme(props.page);

const unitsAndTest = inject<Ref<Page[]>>('unitsAndTest');
const unit = computed(() =>
  unitsAndTest?.value.find(u => u.id === props.page.id)
);

const { compiled: content } = storeToRefs(useContent());
// if this page is hidden, mark it as complete so we can progress past it
if (isHidden(props.page.id)) setCompleted(props.page.id);

provide('unit', unit);

async function onEnter() {
  stopAudio();
  const page = pageEl.value;
  if (page?.id === 'audio-popup') return;

  if (page && current.value) {
    await nextTick();

    setLocation(page.id);

    try {
      dispatchUcEvent('uc:pageenter', { pageId: page.id });
      window.dispatchEvent(new CustomEvent('pageenter'));
      page.dispatchEvent(new CustomEvent('pageenter--' + page.id));

      const titleEl = page.querySelector<HTMLElement>('h1, h2, h3');

      if (titleEl) {
        const sep = ' / ';
        const t1 = document.title.split(sep)[0].trim();
        const t2 = titleEl.textContent?.trim();
        document.title = t1 === t2 ? t1 : t1 + sep + t2;
      }

      const liveRegion = document.createElement('div');
      liveRegion.classList.add('sr-only');
      liveRegion.textContent = document.title ? document.title : page.title;
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
      setTimeout(() => liveRegion.remove(), 1000);
    } catch (e) {
      console.error(e);
    }
  }
}

const onLeave = () => {
  if (!current.value) {
    dispatchUcEvent('uc:pageleave', { pageId: props.page.id });
  }
};

const openIframes = computed(() =>
  typeof props.page['open-iframes'] === 'boolean'
    ? props.page['open-iframes']
    : unref(inject('open-iframes'))
);

provide('open-iframes', openIframes);

watchEffect(
  () => {
    if (contentEl.value) {
      // wrap onEnter in nextTick so its dependencies arent tracked
      nextTick(onEnter);
    } else {
      nextTick(onLeave);
    }
  },
  { flush: 'post' }
);
</script>

<script lang="ts">
export default { inheritAttrs: false };
</script>

<style>
.page {
  overflow: hidden;
  height: 100vh;
  width: 100vw;
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
  z-index: 1;
  background-size: cover;
  background-attachment: fixed;
  background-position: center;
}

.page-enter-active,
.page-leave-active {
  transition:
    opacity 0.5s linear,
    visibility 0.5s step-start;
}

.page-leave-active {
  z-index: 2;
}

.page-leave-to {
  opacity: 0;
  z-index: 2;
  transition:
    opacity 0.5s linear,
    visibility 0.5s step-end;
}
</style>
